/*
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { ref, shallowRef, computed, onUnmounted } from "vue";
import {
  collab,
  sendableUpdates,
  receiveUpdates,
  getSyncedVersion,
} from "@codemirror/collab";
import {
  ViewPlugin,
  Decoration,
  WidgetType,
  EditorView,
} from "@codemirror/view";
import {
  StateField,
  StateEffect,
  ChangeSet,
  Compartment,
} from "@codemirror/state";
import { useCollabWebSocket, ConnectionState } from "./useCollabWebSocket";
import collabService from "@/services/CollabService";
import collabBackupService from "@/services/CollabBackupService";

// Role constants
export const ROLE_EDITOR = "editor";
export const ROLE_OBSERVER = "observer";
export const PROTOCOL_VERSION = 2;

/**
 * Creates a remote cursor decoration widget
 */
class RemoteCursorWidget extends WidgetType {
  constructor(peer) {
    super();
    this.peer = peer;
  }

  toDOM() {
    const wrapper = document.createElement("span");
    wrapper.className = "cm-remote-cursor";
    wrapper.style.borderLeft = `2px solid ${this.peer.color}`;
    wrapper.style.marginLeft = "-1px";
    wrapper.style.position = "relative";

    const label = document.createElement("span");
    label.className = "cm-remote-cursor-label";
    label.style.backgroundColor = this.peer.color;
    label.style.color = "#fff";
    label.style.padding = "1px 4px";
    label.style.borderRadius = "3px";
    label.style.fontSize = "10px";
    label.style.position = "absolute";
    label.style.top = "-18px";
    label.style.left = "0";
    label.style.whiteSpace = "nowrap";
    label.textContent = this.peer.displayName;

    wrapper.appendChild(label);
    return wrapper;
  }
}

/**
 * State effect for updating remote cursors
 */
const updateRemoteCursors = StateEffect.define();

/**
 * State field for tracking remote cursors
 */
const remoteCursorsField = StateField.define({
  create() {
    return [];
  },
  update(cursors, tr) {
    for (const effect of tr.effects) {
      if (effect.is(updateRemoteCursors)) {
        return effect.value;
      }
    }
    return cursors;
  },
});

/**
 * Creates decorations for remote cursors
 */
const remoteCursorsPlugin = ViewPlugin.fromClass(
  class {
    constructor(view) {
      this.decorations = this.createDecorations(view);
    }

    update(update) {
      if (
        update.docChanged ||
        update.state.field(remoteCursorsField) !==
          update.startState.field(remoteCursorsField)
      ) {
        this.decorations = this.createDecorations(update.view);
      }
    }

    createDecorations(view) {
      const cursors = view.state.field(remoteCursorsField);
      const decorations = [];

      for (const cursor of cursors) {
        if (cursor.position >= 0 && cursor.position <= view.state.doc.length) {
          decorations.push(
            Decoration.widget({
              widget: new RemoteCursorWidget(cursor),
              side: 1,
            }).range(cursor.position),
          );

          // Add selection highlight if there's a selection
          if (cursor.anchor !== cursor.position) {
            const from = Math.min(cursor.anchor, cursor.position);
            const to = Math.max(cursor.anchor, cursor.position);
            decorations.push(
              Decoration.mark({
                class: "cm-remote-selection",
                attributes: { style: `background-color: ${cursor.color}33` },
              }).range(from, to),
            );
          }
        }
      }

      return Decoration.set(decorations, true);
    }
  },
  {
    decorations: (v) => v.decorations,
  },
);

/**
 * Vue composable for collaborative editing with CodeMirror.
 * Integrates @codemirror/collab with WebSocket communication.
 *
 * @returns {Object} Composable API
 */
export function useCollabEditor() {
  // State
  const isCollabActive = ref(false);
  const sessionId = ref(null);
  const clientId = ref(null);
  const myColor = ref(null);
  const myRole = ref(null);
  const connectedPeers = shallowRef([]);
  const remoteCursors = shallowRef([]);
  const startVersion = ref(0);
  const lastError = ref(null);
  const isSessionFull = ref(false);
  const isInvalidDocument = ref(false);
  const isProtocolIncompatible = ref(false);
  // True when the Alfresco ticket has expired/become invalid and re-authentication keeps failing.
  // Used to stop the reconnection loop and tell the user to reload instead of retrying forever.
  const isAuthExpired = ref(false);
  const permissionLevel = ref(null);

  // Callback for document updates (used in observer mode)
  let onDocumentUpdate = null;

  // Callback when document is saved (by any peer)
  let onSavedCallback = null;

  // WebSocket composable
  const {
    connectionState,
    lastCloseCode,
    connect,
    disconnect,
    send,
    on,
    off,
    resetReconnectAttempts,
  } = useCollabWebSocket();

  // Editor view reference (set by consuming component)
  let editorView = null;
  let nodeId = null;
  let pushTimeout = null;

  // Reconnection state
  let reconnectDisplayName = null;
  let reconnectMode = null;
  let backupTimer = null;

  // Editable compartment for toggling editor on disconnect
  const editableCompartment = new Compartment();

  // Callback for resync (version mismatch / reconnection)
  let onResyncCallback = null;

  /**
   * Updates the editor view reference.
   * Must be called after recreating the editor with collab extensions.
   *
   * @param {EditorView} view - The new editor view
   */
  const setEditorView = (view) => {
    editorView = view;
  };

  // Computed
  const peerCount = computed(() => connectedPeers.value.length);
  const isConnected = computed(
    () => connectionState.value === ConnectionState.CONNECTED,
  );
  const isReconnecting = computed(
    () => connectionState.value === ConnectionState.RECONNECTING,
  );
  const isConnectionFailed = computed(
    () => connectionState.value === ConnectionState.ERROR,
  );
  const isEditor = computed(() => myRole.value === ROLE_EDITOR);
  const isObserver = computed(() => myRole.value === ROLE_OBSERVER);
  const editorCount = computed(
    () => connectedPeers.value.filter((p) => p.role === ROLE_EDITOR).length,
  );
  const observerCount = computed(
    () => connectedPeers.value.filter((p) => p.role === ROLE_OBSERVER).length,
  );
  const hasWritePermission = computed(() => permissionLevel.value === "WRITE");
  const hasReadPermission = computed(
    () => permissionLevel.value === "READ" || permissionLevel.value === "WRITE",
  );

  /**
   * Creates the collaborative extensions for CodeMirror
   *
   * @param {number} version - Starting document version
   * @returns {Extension[]} CodeMirror extensions
   */
  const createCollabExtensions = (version) => {
    return [
      collab({
        startVersion: version,
        clientID: clientId.value,
        sharedEffects: (tr) => {
          // Share cursor position changes
          if (tr.selection) {
            const sel = tr.selection.main;
            scheduleCursorUpdate(sel.head, sel.anchor);
          }
          return [];
        },
      }),
      remoteCursorsField,
      remoteCursorsPlugin,
      editableCompartment.of(EditorView.editable.of(true)),
    ];
  };

  /**
   * Authenticates the WebSocket connection by sending an auth message
   * and waiting for the authenticated response.
   *
   * @param {string} ticket - The Alfresco authentication ticket
   * @returns {Promise<void>} Resolves when authenticated
   */
  const authenticateWebSocket = async (ticket) => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        off("onMessage", authHandler);
        reject(new Error("Authentication timeout"));
      }, 5000);

      const authHandler = (msg) => {
        if (msg.type === "authenticated") {
          clearTimeout(timeout);
          off("onMessage", authHandler);
          resolve();
        } else if (msg.type === "error" && msg.code === "AUTH_FAILED") {
          clearTimeout(timeout);
          off("onMessage", authHandler);
          const authError = new Error(msg.message || "Authentication failed");
          authError.code = "AUTH_FAILED";
          reject(authError);
        }
      };

      on("onMessage", authHandler);
      send({ type: "auth", ticket });
    });
  };

  /**
   * Starts collaborative editing for a document
   *
   * @param {string} nodeIdParam - Alfresco node ID
   * @param {EditorView} view - CodeMirror editor view (can be null for observer mode)
   * @param {string} displayName - User's display name
   * @param {string} mode - Role mode: "editor" or "observer" (default: "observer")
   * @param {Function} documentUpdateCallback - Callback when document is updated (for observers)
   * @param {Function} savedCallback - Callback when document is saved (by any peer)
   * @returns {Promise<Object>} Welcome message with session info
   */
  const startCollab = async (
    nodeIdParam,
    view,
    displayName,
    mode = ROLE_OBSERVER,
    documentUpdateCallback = null,
    savedCallback = null,
    resyncCallback = null,
  ) => {
    if (isCollabActive.value) {
      await stopCollab();
    }

    isSessionFull.value = false;
    isInvalidDocument.value = false;
    isAuthExpired.value = false;

    try {
      nodeId = nodeIdParam;
      editorView = view;
      clientId.value = collabService.generateClientId();
      onDocumentUpdate = documentUpdateCallback;
      onSavedCallback = savedCallback;
      onResyncCallback = resyncCallback;
      reconnectDisplayName = displayName;
      reconnectMode = mode;

      // Check availability with permission level
      const availability = await collabService.checkAvailability(nodeId);
      // Default to WRITE when backend does not return permissionLevel (backward compatibility)
      permissionLevel.value = availability.permissionLevel || "WRITE";

      if (!availability.enabled) {
        throw new Error(
          "Collaborative editing is not available for this document",
        );
      }

      // No access at all
      if (availability.permissionLevel === "NONE") {
        throw new Error("Access denied to this document");
      }

      // Force observer mode for read-only users
      if (availability.permissionLevel === "READ" && mode === ROLE_EDITOR) {
        mode = ROLE_OBSERVER;
      }

      // Get WebSocket URL (no longer async, token not in URL)
      const wsUrl = collabService.getWebSocketUrl();

      // Set up message handlers before connecting
      on("onMessage", handleMessage);
      on("onDisconnected", handleDisconnect);
      on("onReconnecting", handleReconnecting);
      on("onConnected", handleReconnected);
      await connect(wsUrl);

      // Authenticate via WebSocket message (token not exposed in URL)
      const ticket = await collabService.getTicket();
      await authenticateWebSocket(ticket);

      // Send join message
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Timeout waiting for welcome message"));
        }, 10000);

        const welcomeHandler = (message) => {
          if (message.type === "welcome") {
            clearTimeout(timeout);
            off("onMessage", welcomeHandler);
            handleWelcome(message);
            resolve(message);
          } else if (message.type === "error") {
            clearTimeout(timeout);
            off("onMessage", welcomeHandler);
            if (message.code === "SESSION_FULL") {
              isSessionFull.value = true;
            } else if (message.code === "INVALID_DOCUMENT") {
              isInvalidDocument.value = true;
            }
            reject(new Error(message.message));
          }
        };

        on("onMessage", welcomeHandler);

        send({
          type: "join",
          nodeId,
          clientId: clientId.value,
          displayName,
          mode,
          protocolVersion: PROTOCOL_VERSION,
        });
      });
    } catch (error) {
      lastError.value = error;
      throw error;
    }
  };

  /**
   * Stops collaborative editing
   */
  const stopCollab = async () => {
    if (!isCollabActive.value) {
      return;
    }

    if (pushTimeout) {
      clearTimeout(pushTimeout);
      pushTimeout = null;
    }

    if (backupTimer) {
      clearInterval(backupTimer);
      backupTimer = null;
    }

    // Save final backup before leaving
    if (editorView && nodeId) {
      const content = editorView.state.doc.toString();
      await collabBackupService
        .saveBackup(nodeId, content, sessionId.value)
        .catch(() => {});
    }

    // Send leave message
    if (sessionId.value && clientId.value) {
      send({
        type: "leave",
        sessionId: sessionId.value,
        clientId: clientId.value,
      });
    }

    // Unregister handlers before disconnect
    off("onMessage", handleMessage);
    off("onDisconnected", handleDisconnect);
    off("onReconnecting", handleReconnecting);
    off("onConnected", handleReconnected);

    disconnect();

    // Reset state
    isCollabActive.value = false;
    sessionId.value = null;
    connectedPeers.value = [];
    remoteCursors.value = [];
    myColor.value = null;
    myRole.value = null;
    permissionLevel.value = null;
    onDocumentUpdate = null;
    onSavedCallback = null;
    onResyncCallback = null;
    reconnectDisplayName = null;
    reconnectMode = null;
    editorView = null;
    nodeId = null;
  };

  /**
   * Handles welcome message from server
   */
  const handleWelcome = (message) => {
    sessionId.value = message.sessionId;
    startVersion.value = message.version;
    myColor.value = message.color;
    myRole.value = message.role || ROLE_OBSERVER;
    connectedPeers.value = message.peers || [];
    isCollabActive.value = true;

    // Update remote cursors
    updateRemoteCursorsState();

    // Start periodic backup (every 10 seconds)
    if (backupTimer) {
      clearInterval(backupTimer);
    }
    backupTimer = setInterval(() => {
      if (editorView && nodeId) {
        const content = editorView.state.doc.toString();
        collabBackupService
          .saveBackup(nodeId, content, sessionId.value)
          .catch(() => {});
      }
    }, 10000);
  };

  /**
   * Serializes a CodeMirror ChangeSet to JSON for network transmission.
   * Uses the native toJSON() method to preserve the correct format.
   *
   * @param {ChangeSet} changeSet - The CodeMirror ChangeSet
   * @returns {any} JSON representation of the ChangeSet
   */
  const serializeChanges = (changeSet) => {
    return changeSet.toJSON();
  };

  /**
   * Deserializes JSON back to a CodeMirror ChangeSet.
   * Uses the native fromJSON() method to reconstruct the ChangeSet.
   *
   * @param {any} json - JSON representation from the server
   * @returns {ChangeSet} The CodeMirror ChangeSet
   */
  const deserializeChanges = (json) => {
    return ChangeSet.fromJSON(json);
  };

  /**
   * Handles incoming messages from the server
   */
  const handleMessage = (message) => {
    switch (message.type) {
      case "updates":
        handleUpdates(message);
        break;
      case "peers":
        handlePeers(message);
        break;
      case "cursor":
        handleCursor(message);
        break;
      case "promoted":
        handlePromoted(message);
        break;
      case "saved":
        handleSaved(message);
        break;
      case "resync":
        handleResync(message);
        break;
      case "error":
        handleError(message);
        break;
    }
  };

  /**
   * Handles document updates from the server
   */
  const handleUpdates = (message) => {
    // If we're an observer without an editor, notify via callback
    if (!editorView && onDocumentUpdate && message.document !== undefined) {
      onDocumentUpdate(message.document, message.version);
      return;
    }

    if (!editorView) {
      return;
    }

    // Convert server format to CodeMirror format using native deserialization
    const updates = message.updates.map((update) => ({
      changes: deserializeChanges(update.changes),
      clientID: update.clientID,
    }));

    // Receive updates using @codemirror/collab
    try {
      const transaction = receiveUpdates(editorView.state, updates);
      editorView.dispatch(transaction);

      // After receiving updates, check if we have unconfirmed changes
      // that need to be re-pushed (e.g. stale push from debounce after version mismatch)
      const pending = sendableUpdates(editorView.state);
      if (pending.length > 0) {
        schedulePushChanges();
      }
    } catch (err) {
      console.error("[CollabEditor] Error applying updates:", err);
    }
  };

  /**
   * Handles peer list updates
   */
  const handlePeers = (message) => {
    connectedPeers.value = message.peers.filter(
      (p) => p.clientId !== clientId.value,
    );
    updateRemoteCursorsState();
  };

  /**
   * Handles cursor position updates from other peers
   */
  const handleCursor = (message) => {
    if (message.clientId === clientId.value) {
      return;
    }

    // Update cursor in the peers list
    const peer = connectedPeers.value.find(
      (p) => p.clientId === message.clientId,
    );
    if (peer) {
      peer.cursorPosition = message.position;
      peer.cursorAnchor = message.anchor;
      updateRemoteCursorsState();
    }
  };

  /**
   * Handles error messages
   */
  const handleError = (message) => {
    lastError.value = new Error(message.message);
    console.error(
      "[CollabEditor] Server error:",
      message.code,
      message.message,
      message.details,
    );

    if (message.code === "SESSION_FULL") {
      isSessionFull.value = true;
      stopCollab();
    } else if (message.code === "INVALID_DOCUMENT") {
      isInvalidDocument.value = true;
      stopCollab();
    } else if (message.code === "ACCESS_DENIED") {
      stopCollab();
    } else if (message.code === "INCOMPATIBLE_PROTOCOL") {
      isProtocolIncompatible.value = true;
      stopCollab();
    } else if (message.code === "AUTH_FAILED") {
      // Expired/invalid Alfresco ticket: re-authentication will keep failing and the server keeps
      // closing the connection (code 1008), so retrying is an endless loop. Stop and surface an
      // actionable state — the user must reload to re-authenticate.
      isAuthExpired.value = true;
      stopCollab();
    }
  };

  /**
   * Handles promotion confirmation from server
   */
  const handlePromoted = (message) => {
    myRole.value = ROLE_EDITOR;
    startVersion.value = message.version;

    // Notify callback with current document for editor sync
    if (onDocumentUpdate && message.document !== undefined) {
      onDocumentUpdate(message.document, message.version);
    }
  };

  /**
   * Handles saved confirmation from server (broadcast to all peers)
   */
  const handleSaved = (message) => {
    // Clear backup on successful save
    if (nodeId) {
      collabBackupService.clearBackup(nodeId).catch(() => {});
    }
    if (onSavedCallback) {
      onSavedCallback(message);
    }
  };

  /**
   * Toggles editor editability via the editable compartment
   */
  const setCollabEditable = (editable) => {
    if (editorView) {
      try {
        editorView.dispatch({
          effects: editableCompartment.reconfigure(
            EditorView.editable.of(editable),
          ),
        });
      } catch (e) {
        console.warn("[CollabEditor] Failed to toggle editable:", e);
      }
    }
  };

  /**
   * Handles disconnection — freeze editor input and save backup
   */
  const handleDisconnect = () => {
    setCollabEditable(false);
    // Save backup on disconnect
    if (editorView && nodeId) {
      const content = editorView.state.doc.toString();
      collabBackupService
        .saveBackup(nodeId, content, sessionId.value)
        .catch(() => {});
    }
  };

  /**
   * Handles reconnection attempts
   */
  const handleReconnecting = (attempt, delay) => {
    console.log(
      `[CollabEditor] Reconnecting in ${delay}ms (attempt ${attempt})`,
    );
  };

  /**
   * Handles successful reconnection — re-authenticate and re-join
   */
  const handleReconnected = async () => {
    if (!isCollabActive.value || !nodeId) {
      return;
    }

    try {
      // Re-authenticate with a fresh ticket
      const ticket = await collabService.getTicket();
      await authenticateWebSocket(ticket);

      // Re-join the session
      send({
        type: "join",
        nodeId,
        clientId: clientId.value,
        displayName: reconnectDisplayName,
        mode: reconnectMode,
        protocolVersion: PROTOCOL_VERSION,
      });

      // Restore editability
      setCollabEditable(true);
    } catch (err) {
      console.error(
        "[CollabEditor] Failed to re-authenticate on reconnect:",
        err,
      );
      // AUTH_FAILED is handled centrally in handleError (stops the reconnection loop and sets
      // isAuthExpired); transient failures fall through and the connection retries normally.
    }
  };

  /**
   * Handles resync message from server (version mismatch)
   */
  const handleResync = (message) => {
    startVersion.value = message.version;
    connectedPeers.value = message.peers || [];
    updateRemoteCursorsState();

    // Notify callback with new document state
    if (onResyncCallback) {
      onResyncCallback(message.document, message.version);
    } else if (onDocumentUpdate && message.document !== undefined) {
      onDocumentUpdate(message.document, message.version);
    }
  };

  /**
   * Pushes local changes to the server
   * Following the official CodeMirror collab pattern:
   * https://codemirror.net/examples/collab/
   */
  const pushChanges = () => {
    if (!editorView || !isCollabActive.value || !isConnected.value) {
      return;
    }

    const updates = sendableUpdates(editorView.state);
    if (updates.length === 0) {
      return;
    }

    const version = getSyncedVersion(editorView.state);

    // Serialize updates using native toJSON() - this is the correct format
    // that CodeMirror expects when receiving updates back
    const serializedUpdates = updates.map((u) => ({
      clientID: u.clientID,
      changes: serializeChanges(u.changes),
    }));

    // Also send the current document for observers
    const document = editorView.state.doc.toString();

    send({
      type: "push",
      sessionId: sessionId.value,
      clientId: clientId.value,
      version,
      updates: serializedUpdates,
      document,
    });
  };

  /**
   * Schedules pushing changes (debounced)
   */
  const schedulePushChanges = () => {
    if (pushTimeout) {
      clearTimeout(pushTimeout);
    }
    pushTimeout = setTimeout(pushChanges, 50);
  };

  /**
   * Schedules cursor position update
   */
  let cursorTimeout = null;
  const scheduleCursorUpdate = (position, anchor) => {
    if (cursorTimeout) {
      clearTimeout(cursorTimeout);
    }
    cursorTimeout = setTimeout(() => {
      if (isCollabActive.value && isConnected.value) {
        send({
          type: "cursor",
          sessionId: sessionId.value,
          clientId: clientId.value,
          position,
          anchor,
        });
      }
    }, 100);
  };

  /**
   * Updates remote cursors state in the editor
   */
  const updateRemoteCursorsState = () => {
    if (!editorView) {
      return;
    }

    const cursors = connectedPeers.value
      .filter((p) => p.cursorPosition !== undefined)
      .map((p) => ({
        clientId: p.clientId,
        displayName: p.displayName,
        color: p.color,
        position: p.cursorPosition,
        anchor: p.cursorAnchor || p.cursorPosition,
      }));

    remoteCursors.value = cursors;

    editorView.dispatch({
      effects: updateRemoteCursors.of(cursors),
    });
  };

  /**
   * Creates an update listener for pushing changes
   */
  const createUpdateListener = () => {
    return (update) => {
      if (update.docChanged && isCollabActive.value) {
        schedulePushChanges();
      }
    };
  };

  /**
   * Requests promotion from observer to editor role
   *
   * @returns {Promise<Object>} Promoted message with current document state
   */
  const promoteToEditor = () => {
    return new Promise((resolve, reject) => {
      if (!isCollabActive.value || !isConnected.value) {
        reject(new Error("Not connected to collaborative session"));
        return;
      }

      if (myRole.value === ROLE_EDITOR) {
        resolve({ role: ROLE_EDITOR });
        return;
      }

      const timeout = setTimeout(() => {
        off("onMessage", promoteHandler);
        reject(new Error("Timeout waiting for promotion confirmation"));
      }, 10000);

      const promoteHandler = (message) => {
        if (message.type === "promoted") {
          clearTimeout(timeout);
          off("onMessage", promoteHandler);
          resolve(message);
        } else if (message.type === "error" && message.context === "promote") {
          clearTimeout(timeout);
          off("onMessage", promoteHandler);
          reject(new Error(message.message));
        }
      };

      on("onMessage", promoteHandler);

      send({
        type: "promote",
        sessionId: sessionId.value,
        clientId: clientId.value,
      });
    });
  };

  /**
   * Requests the server to save the document to Alfresco
   *
   * @returns {Promise<void>}
   */
  const requestSave = () => {
    return new Promise((resolve, reject) => {
      if (!isCollabActive.value || !isConnected.value) {
        reject(new Error("Not connected to collaborative session"));
        return;
      }

      const timeout = setTimeout(() => {
        off("onMessage", saveHandler);
        reject(new Error("Timeout waiting for save confirmation"));
      }, 10000);

      const saveHandler = (message) => {
        if (message.type === "saved") {
          clearTimeout(timeout);
          off("onMessage", saveHandler);
          resolve(message);
        } else if (message.type === "error" && message.context === "save") {
          clearTimeout(timeout);
          off("onMessage", saveHandler);
          reject(new Error(message.message));
        }
      };

      on("onMessage", saveHandler);

      send({
        type: "save",
        sessionId: sessionId.value,
        clientId: clientId.value,
      });
    });
  };

  // Cleanup on unmount
  onUnmounted(() => {
    stopCollab();
  });

  return {
    // State
    isCollabActive,
    isSessionFull,
    isInvalidDocument,
    isProtocolIncompatible,
    isAuthExpired,
    permissionLevel,
    sessionId,
    clientId,
    myColor,
    myRole,
    connectedPeers,
    remoteCursors,
    connectionState,
    lastError,
    startVersion,

    // Computed
    peerCount,
    isConnected,
    isReconnecting,
    isConnectionFailed,
    isEditor,
    isObserver,
    editorCount,
    observerCount,
    hasWritePermission,
    hasReadPermission,

    // Methods
    startCollab,
    stopCollab,
    setEditorView,
    createCollabExtensions,
    createUpdateListener,
    pushChanges,
    requestSave,
    promoteToEditor,
    retryConnection: async () => {
      resetReconnectAttempts();
      await handleReconnected();
    },
    lastCloseCode,
  };
}

export default useCollabEditor;
